import {
  Stack,
  Divider,
  Group,
  ActionIcon,
  Paper,
  Textarea,
  LoadingOverlay,
  Text,
  Center,
  Loader,
  TextInput,
} from "@mantine/core"
import {
  IconChevronDown,
  IconChevronUp,
  IconPaperclip,
  IconSearch,
  IconSend,
  IconTrash,
  IconX,
} from "@tabler/icons-react"
import { useDebouncedCallback, useDebouncedValue } from "@mantine/hooks"
import { getPageIndex, useSearchChatPosts } from "@/orval/gen"
import { ChatMessagePaper } from "./ChatMessagePaper"
import dayjs from "dayjs"
import { ChatHeaderGroup } from "./ChatHeaderGroup"
import { MessageListItem } from "./MessageListItem"
import { useTranslation } from "react-i18next"
import { ChatType, GetUnreadPageIndexResult } from "../../../orval/gen/model"
import { FilePreview } from "../../../-components/FilePreview"
import { SuspenseWithErrorBoundary } from "../../../-components/suspense"
import { EmojiPickerDropdown } from "../../app/-components/EmojiPickerDropdown.tsx"
import { DeviceType, useDeviceType } from "../../../utils/useDeviceType.ts"
import { DefaultError } from "../../../-components/error-context/index.tsx"
import { useDefaultSuspenseInfiniteQuery } from "../../../utils/useDefaultSuspenseInfiniteQuery.ts"
import { notifications } from "@mantine/notifications"
import { useGetAuthenticatedUser } from "../../app/-components/use-authenticated-user.tsx"
import { useUserLocalStorage } from "@/routes/app/-components/use-user-local-storage.tsx"
import { showErrorNotification } from "@/utils/form-submission-notifications.ts"
import { useChatPostAttachmentApi } from "@/attachment-api/chatPostAttachmentApi.tsx"
import {
  useDefaultFileDialog,
  defaultAcceptedMIMETypes,
} from "../useDefaultFileDialog.tsx"
import {
  getChatPosts,
  getGetChatPostsQueryKey,
  useCreateChatPost,
  useUpdateReadMessage,
} from "@/orval/gen"
import { ChatDto, ChatPostDto } from "@/orval/gen/model/index.ts"
import { debugLog } from "@/utils/debugLog.tsx"
import { BidirectionalInfiniteList } from "@components/BidirectionalInfiniteScrollingList.tsx"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { useBreakpoints } from "@/utils/use-breakpoints.tsx"

const READ_UPDATE_DEBOUNCE_MS = 1000
const EMPTY_SEARCH_RESULTS: number[] = []

interface ChatProps {
  unreadState: GetUnreadPageIndexResult
  chat: ChatDto
  pageSize: number
}

interface SearchTarget {
  chatId: number
  messageId: number
  pageIndex: number
}

export function Chat({ unreadState, chat, pageSize }: ChatProps) {
  const { t } = useTranslation("app_server")
  const { chatId } = chat
  const openingStateRef = React.useRef({
    chatId,
    unreadCount: unreadState.newPostsCount ?? 0,
  })
  if (openingStateRef.current.chatId !== chatId) {
    openingStateRef.current = {
      chatId,
      unreadCount: unreadState.newPostsCount ?? 0,
    }
  }

  const routeSearch = useSearch({ from: "/app/com/chat/$chatId" })
  const navigate = useNavigate({ from: "/app/com/chat/$chatId" })
  const searchOpened = routeSearch.messageSearch !== undefined
  const [debouncedSearch] = useDebouncedValue(
    routeSearch.messageSearch?.trim() ?? "",
    300
  )
  const [searchTarget, setSearchTarget] = React.useState<SearchTarget>()
  const searchNavigationRequest = React.useRef(0)
  const searchQuery = useSearchChatPosts(
    { chatId, search: debouncedSearch },
    { query: { enabled: searchOpened && debouncedSearch.length > 0 } }
  )
  const searchResults = searchQuery.data ?? EMPTY_SEARCH_RESULTS
  const activeSearchIndex =
    searchResults.length > 0
      ? Math.min(routeSearch.searchResult ?? 0, searchResults.length - 1)
      : -1
  const activeSearchMessageId = searchResults[activeSearchIndex]
  const activeSearchTarget =
    searchOpened &&
    searchTarget?.chatId === chatId &&
    searchTarget.messageId === activeSearchMessageId
      ? searchTarget
      : undefined

  React.useEffect(() => {
    const messageId = activeSearchMessageId
    if (!searchOpened || !messageId) {
      searchNavigationRequest.current++
      setSearchTarget(undefined)
      return
    }

    const request = ++searchNavigationRequest.current
    void getPageIndex({ chatId, messageId, pageSize }).then((result) => {
      if (request !== searchNavigationRequest.current) return
      setSearchTarget({
        chatId,
        messageId,
        pageIndex: result.pageIndex ?? 0,
      })
    })
  }, [activeSearchMessageId, chatId, searchOpened])

  const setSearchResult = (searchResult: number) => {
    void navigate({
      replace: true,
      search: (previous) => ({ ...previous, searchResult }),
    })
  }

  const closeSearch = () => {
    searchNavigationRequest.current++
    setSearchTarget(undefined)
    void navigate({
      replace: true,
      search: (previous) => ({
        ...previous,
        messageSearch: undefined,
        searchResult: undefined,
      }),
    })
  }

  const [message, setMessage] = useUserLocalStorage({
    key: "chat-" + chatId + "-message",
    defaultValue: "",
    sync: false,
  })

  const [replyToMessage, setReplyToMessage] =
    useUserLocalStorage<null | ChatPostDto>({
      key: "chat-" + chatId + "-reply",
      defaultValue: null,
      sync: false,
    })

  const [isDragging, setIsDragging] = React.useState(false)

  function resetMessage() {
    setMessage("")
    setReplyToMessage(null)
    setFiles(null)
  }

  const [sendedMessage, setSendedMessage] = React.useState(false)

  const { mutateAsync: sendMessage, isPending } = useCreateChatPost({
    mutation: {
      onSuccess: async (chatPostId) => {
        if (files) {
          files.forEach(async (file) => {
            try {
              await chatPostAttchmentApi.addAttachmentAndMetadata(
                file,
                {
                  chatPostId,
                },
                chatPostId
              )
            } catch (err) {
              console.error(err)
              if (err as DefaultError) {
                const error = err as DefaultError
                showErrorNotification(error.message)
              }
            }
          })
          setFiles(null)
        }

        setSendedMessage(true)
        resetMessage()
      },
      onError: showErrorNotification,
    },
  })

  const chatPostAttchmentApi = useChatPostAttachmentApi()
  const handleSendMessage = async () => {
    debugLog("handleSendMessage", { message, files })
    if (message.length > 0 || (files && files?.length > 0)) {
      await sendMessage({
        data: {
          chatId: chat.chatId,
          replyToMessageId: replyToMessage?.chatPostId,
          message,
        },
      })
    }
  }

  const { xs, sm, md } = useBreakpoints()
  const isSmallLayout = xs || sm || md

  const textareaRef = React.useRef<HTMLTextAreaElement>(null)

  const device = useDeviceType()

  const currentUserId = useGetAuthenticatedUser().data.userId
  const chatMember = chat.chatMembers.find((cm) => cm.userId === currentUserId)
  const readOnly =
    (chat.chatType === "Broadcast" || chat.isReadOnly) && !chatMember?.isAdmin

  React.useEffect(() => {
    device === DeviceType.Desktop && textareaRef.current?.focus()
    setFiles(null)
  }, [chatId])

  const allowedMIMETypes = defaultAcceptedMIMETypes

  const { files, fileDialog, setFiles } = useDefaultFileDialog({
    acceptedMIMETypes: allowedMIMETypes,
    maxSize: 10,
    multiple: true,
  })

  const handleGlobalDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const dropped = e.dataTransfer?.files
    if (!dropped || dropped.length === 0) return

    const validFiles: File[] = []
    const invalidFiles: string[] = []

    for (let i = 0; i < dropped.length; i++) {
      const f = dropped.item(i)
      if (!f) continue
      if (!f.type || !allowedMIMETypes.includes(f.type)) {
        invalidFiles.push(f.name)
      } else {
        validFiles.push(f)
      }
    }

    if (invalidFiles.length > 0) {
      showErrorNotification(new Error(t("InvalidFile")))
    }

    if (validFiles.length > 0) {
      setFiles([...(files ?? []), ...validFiles])
    }
  }

  const SearchGroup = React.memo(() => (
    <Group gap={4} wrap="nowrap" onClick={(event) => event.stopPropagation()}>
      <TextInput
        autoFocus
        size="xs"
        w={240}
        value={routeSearch.messageSearch ?? ""}
        placeholder={t("Search")}
        leftSection={<IconSearch size={14} />}
        onChange={(event) => {
          const messageSearch = event.currentTarget.value
          void navigate({
            replace: true,
            search: (previous) => ({
              ...previous,
              messageSearch,
              searchResult: undefined,
            }),
          })
        }}
      />
      <Text size="sm" miw={45} ta="center">
        {searchQuery.isFetching
          ? "…"
          : activeSearchIndex >= 0
            ? `${activeSearchIndex + 1}/${searchResults.length}`
            : `0/${searchResults.length}`}
      </Text>
      <ActionIcon
        variant="subtle"
        aria-label={t("Back")}
        title={t("Back")}
        disabled={
          activeSearchIndex < 0 || activeSearchIndex >= searchResults.length - 1
        }
        onClick={() => setSearchResult(activeSearchIndex + 1)}
      >
        <IconChevronUp size={18} />
      </ActionIcon>
      <ActionIcon
        variant="subtle"
        aria-label={t("Next")}
        title={t("Next")}
        disabled={activeSearchIndex <= 0}
        onClick={() => setSearchResult(activeSearchIndex - 1)}
      >
        <IconChevronDown size={18} />
      </ActionIcon>
      <ActionIcon
        variant="subtle"
        aria-label={t("Close")}
        title={t("Close")}
        onClick={closeSearch}
      >
        <IconX size={18} />
      </ActionIcon>
    </Group>
  ))

  return (
    <>
      <Stack
        pb={xs || sm ? "xs" : undefined}
        key={"stack-" + chatId}
        h="100%"
        w="100%"
        gap={0}
        flex="1"
        onDragOver={(e) => {
          e.preventDefault()
          setIsDragging(true)
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleGlobalDrop}
        style={{ position: "relative" }}
      >
        <ChatHeaderGroup
          chatId={chatId}
          rightSection={
            searchOpened && !isSmallLayout ? (
              <SearchGroup />
            ) : (
              <ActionIcon
                variant="subtle"
                aria-label={t("Search")}
                title={t("Search")}
                onClick={(event) => {
                  event.stopPropagation()
                  void navigate({
                    replace: true,
                    search: (previous) => ({
                      ...previous,
                      messageSearch: "",
                      searchResult: undefined,
                    }),
                  })
                }}
              >
                <IconSearch size={18} />
              </ActionIcon>
            )
          }
        />

        {chat.isHidden && !chatMember?.isAdmin ? (
          <Paper mih={0} flex="1">
            <Group p="md">
              <Text>{t("ChatNotAvailable")}</Text>
            </Group>
          </Paper>
        ) : (
          <>
            <Paper mih={0} flex="1">
              {searchOpened && isSmallLayout ? <SearchGroup /> : null}
              <SuspenseWithErrorBoundary>
                <ChatList
                  pageSize={pageSize}
                  key={`${chatId}-${activeSearchTarget?.messageId ?? "opening"}`}
                  currentUserId={currentUserId}
                  chat={chat}
                  readOnly={readOnly}
                  onReplyButtonClick={(message) => {
                    textareaRef.current?.focus()
                    setReplyToMessage(message)
                  }}
                  newPostsCount={unreadState.newPostsCount}
                  firstUnreadMessageId={unreadState.firstUnreadMessageId}
                  firstUnreadMessagePageIndex={
                    unreadState.firstUnreadMessagePageIndex ?? undefined
                  }
                  openingUnreadCount={openingStateRef.current.unreadCount}
                  searchTarget={activeSearchTarget}
                  searchTerm={searchOpened ? debouncedSearch : ""}
                  activeSearchMessageId={activeSearchMessageId}
                  sendedMessage={sendedMessage}
                />
              </SuspenseWithErrorBoundary>
            </Paper>
            <Stack gap={5}>
              {replyToMessage ? (
                <Group pr="md">
                  <ChatMessagePaper
                    flex="1 0 0"
                    showReadIndicator={
                      chat.chatType !== "Broadcast" &&
                      replyToMessage.userId === currentUserId
                    }
                    w="0"
                    message={replyToMessage}
                  />
                  <ActionIcon
                    style={{ pointerEvents: "all" }}
                    onClick={() => setReplyToMessage(null)}
                    variant="subtle"
                    color="red"
                  >
                    <IconTrash />
                  </ActionIcon>
                </Group>
              ) : null}
              <Group>
                {files?.length
                  ? files?.map((x, index) => (
                      <Group key={index}>
                        <FilePreview
                          mah={100}
                          maw={200}
                          withDownload={false}
                          src={URL.createObjectURL(x)}
                          fileName={x.name}
                        />
                        <Group>
                          <ActionIcon
                            style={{ pointerEvents: "all" }}
                            variant="subtle"
                            color="red"
                            onClick={() => {
                              setFiles(files.filter((_, i) => i !== index))
                            }}
                          >
                            <IconTrash />
                          </ActionIcon>
                        </Group>
                      </Group>
                    ))
                  : null}
              </Group>
              {!readOnly && (
                <Group gap={5}>
                  <LoadingOverlay visible={isPending} />
                  <Textarea
                    leftSectionProps={{
                      style: { alignItems: "end", paddingBottom: 1 },
                    }}
                    rightSectionProps={{
                      style: { alignItems: "end", paddingBottom: 1 },
                    }}
                    leftSection={
                      <EmojiPickerDropdown
                        position="top"
                        focusTargetRef={textareaRef}
                        actionIconProps={{
                          variant: "subtle",
                          style: {
                            pointerEvents: "all",
                          },
                        }}
                        onSelect={(emoji) => {
                          const cursorPosition =
                            textareaRef.current?.selectionStart ?? 0
                          setMessage((prev) => {
                            const start = prev.slice(0, cursorPosition)
                            const end = prev.slice(cursorPosition)
                            return start + emoji.emoji + end
                          })
                        }}
                      />
                    }
                    rightSection={
                      <ActionIcon
                        onClick={fileDialog.open}
                        variant="subtle"
                        style={{ pointerEvents: "all" }}
                      >
                        <IconPaperclip />
                      </ActionIcon>
                    }
                    ref={textareaRef}
                    flex="1"
                    autosize
                    autoFocus={device === DeviceType.Desktop}
                    maxRows={15}
                    style={{ pointerEvents: "all" }}
                    value={message}
                    onChange={(evt) => setMessage(evt.currentTarget.value)}
                    onKeyDown={(evt) => {
                      if (
                        evt.key === "Enter" &&
                        !evt.shiftKey &&
                        (message.length || files?.length)
                      ) {
                        evt.preventDefault()
                        handleSendMessage()
                      }
                    }}
                    onPaste={(e) => {
                      if (e.clipboardData.files.length) {
                        const pasted = e.clipboardData.files
                        const validFiles: File[] = []
                        const invalidFiles: string[] = []
                        for (let i = 0; i < pasted.length; i++) {
                          const f = pasted.item(i)
                          if (!f) continue
                          if (!f.type || !allowedMIMETypes.includes(f.type)) {
                            invalidFiles.push(f.name)
                          } else {
                            validFiles.push(f)
                          }
                        }
                        if (invalidFiles.length > 0) {
                          showErrorNotification(new Error(t("InvalidFile")))
                        }
                        if (validFiles.length > 0)
                          setFiles([...(files ?? []), ...validFiles])
                      }
                    }}
                  />
                  {message.length > 0 || files?.length ? (
                    <ActionIcon
                      style={{ pointerEvents: "all" }}
                      disabled={!(message.length || files?.length)}
                      onClick={handleSendMessage}
                    >
                      <IconSend />
                    </ActionIcon>
                  ) : null}
                </Group>
              )}
            </Stack>
          </>
        )}

        {isDragging && (
          <Center
            pos={"absolute"}
            top={0}
            left={0}
            w={"100%"}
            h={"100%"}
            style={{
              background:
                "color-mix(in srgb, var(--mantine-color-gray-6) 60%, transparent)",
              zIndex: 9999,
              pointerEvents: "none",
            }}
          >
            <Text color="white">{t("DragAttachmentsHere")}</Text>
          </Center>
        )}
      </Stack>
    </>
  )
}

interface ChatListProps {
  pageSize: number
  chat: ChatDto
  onReplyButtonClick: (message: ChatPostDto) => void
  readOnly: boolean
  currentUserId: number
  newPostsCount?: number
  firstUnreadMessageId?: number | null
  firstUnreadMessagePageIndex?: number
  openingUnreadCount: number
  searchTarget?: SearchTarget
  searchTerm: string
  activeSearchMessageId?: number
  sendedMessage: boolean
}
function ChatList({
  chat,
  onReplyButtonClick,
  readOnly,
  currentUserId,
  newPostsCount,
  firstUnreadMessageId,
  firstUnreadMessagePageIndex,
  openingUnreadCount,
  searchTarget,
  searchTerm,
  activeSearchMessageId,
  sendedMessage,
  pageSize,
}: ChatListProps) {
  const [t] = useTranslation("app_server")
  const { mutateAsync: sendLastReadMessageId } = useUpdateReadMessage()
  const initialUnreadState = React.useRef({
    firstUnreadMessageId,
    count: Math.max(openingUnreadCount, newPostsCount ?? 0),
  }).current
  const openingAnchorMessageId =
    initialUnreadState.firstUnreadMessageId ?? undefined
  const openingPageIndex = firstUnreadMessagePageIndex ?? 0
  const [initialPage, setInitialPage] = React.useState(openingPageIndex)

  const lastSentReadMessageId = React.useRef<number | undefined>(undefined)
  const highestVisibleUnreadMessageId = React.useRef<number | undefined>(
    undefined
  )

  const sendHighestReadMessageId = useDebouncedCallback(async () => {
    const messageId = highestVisibleUnreadMessageId.current
    if (
      messageId === undefined ||
      (lastSentReadMessageId.current !== undefined &&
        messageId <= lastSentReadMessageId.current)
    )
      return

    await sendLastReadMessageId({
      data: {
        chatId: chat.chatId,
        messageId,
      },
    })
    lastSentReadMessageId.current = Math.max(
      lastSentReadMessageId.current ?? messageId,
      messageId
    )
  }, READ_UPDATE_DEBOUNCE_MS)

  const chatMessages = useDefaultSuspenseInfiniteQuery(
    getChatPosts,
    (pageRequest) => ({
      chatId: chat.chatId,
      ...pageRequest,
    }),
    {
      queryKey: [
        ...getGetChatPostsQueryKey({ chatId: chat.chatId }),
        searchTarget
          ? {
              searchMessageId: searchTarget.messageId,
              searchPageIndex: searchTarget.pageIndex,
            }
          : {
              openingMessageId: openingAnchorMessageId,
              openingPageIndex,
            },
      ],
      pageSize,
      initialPageParam: {
        pageParam: searchTarget?.pageIndex ?? initialPage,
        pageSize,
      },
      getPreviousPageParam: (firstPage) => {
        if (firstPage.pageNumber > 0) {
          return { pageParam: firstPage.pageNumber - 1, pageSize }
        }
        return undefined
      },
    }
  )

  const openingOlderPageIndex = openingPageIndex + 1
  const hasOpeningOlderPage =
    searchTarget != null ||
    initialUnreadState.firstUnreadMessageId == null ||
    !chatMessages.hasNextPage ||
    chatMessages.data.pages.some(
      (page) => page.pageNumber === openingOlderPageIndex
    )
  const openingOlderPageRequested = React.useRef(false)

  React.useEffect(() => {
    if (
      hasOpeningOlderPage ||
      openingOlderPageRequested.current ||
      chatMessages.isFetchNextPageError
    )
      return

    openingOlderPageRequested.current = true
    void chatMessages.fetchNextPage()
  }, [
    chatMessages.fetchNextPage,
    chatMessages.isFetchNextPageError,
    hasOpeningOlderPage,
  ])

  const currentMember = chat.chatMembers.find(
    (cm) => cm.userId === currentUserId
  )
  const LastChatPostBeforeJoiningId: number | null | undefined =
    currentMember?.lastChatPostBeforeJoiningId

  React.useEffect(() => {
    notifications.hide("new-message-" + chat.chatId)
  }, [chat.chatId, chatMessages.data])

  const handleMessageInView = (postId: number) => {
    if (
      lastSentReadMessageId.current &&
      postId <= lastSentReadMessageId.current
    )
      return

    highestVisibleUnreadMessageId.current =
      highestVisibleUnreadMessageId.current
        ? Math.max(highestVisibleUnreadMessageId.current, postId)
        : postId
    sendHighestReadMessageId()
  }
  const navigate = useNavigate({ from: "/app/com/chat/$chatId" })
  if (!hasOpeningOlderPage && !chatMessages.isFetchNextPageError) {
    return (
      <Center h="100%">
        <Loader size="sm" />
      </Center>
    )
  }
  return (
    <BidirectionalInfiniteList
      sendedMessage={sendedMessage}
      getItemId={(msg) => msg.chatPostId.toString()}
      initialAnchorId={
        searchTarget
          ? searchTarget.messageId.toString()
          : initialUnreadState.firstUnreadMessageId
            ? initialUnreadState.firstUnreadMessageId.toString()
            : undefined
      }
      anchorMarker={
        initialUnreadState.count > 0 &&
        initialUnreadState.firstUnreadMessageId != null
          ? {
              ...(initialUnreadState.firstUnreadMessageId
                ? {
                    beforeId:
                      initialUnreadState.firstUnreadMessageId.toString(),
                  }
                : {
                    afterId: initialUnreadState.firstUnreadMessageId.toString(),
                  }),
              content: (
                <Divider
                  px="sm"
                  size="sm"
                  labelPosition="center"
                  label={t("NewPostsCount", {
                    count: initialUnreadState.count,
                  })}
                />
              ),
            }
          : undefined
      }
      older={{
        fetch: chatMessages.fetchNextPage,
        hasMore: chatMessages.hasNextPage,
        isFetching: chatMessages.isFetchingNextPage,
      }}
      newer={{
        fetch: chatMessages.fetchPreviousPage,
        hasMore: chatMessages.hasPreviousPage,
        isFetching: chatMessages.isFetchingPreviousPage,
      }}
      queryResult={chatMessages}
      fetchFirstPage={() => {
        setInitialPage(0)
        navigate({
          search: (search) => ({ ...search, searchResult: undefined }),
        })
        return chatMessages.refetch()
      }}
      divider={(prev, curr) => {
        if (!curr) {
          return
        }
        if (
          (LastChatPostBeforeJoiningId !== undefined &&
            LastChatPostBeforeJoiningId !== null &&
            curr.chatPostId <= LastChatPostBeforeJoiningId) ||
          (prev &&
            LastChatPostBeforeJoiningId !== undefined &&
            LastChatPostBeforeJoiningId !== null &&
            prev.chatPostId <= LastChatPostBeforeJoiningId)
        ) {
          return
        }

        const currentDate = dayjs(curr?.timeStamp)
        const startsNewDate =
          prev === undefined ||
          !dayjs(prev.timeStamp).isSame(currentDate, "day")
        if (!startsNewDate) return

        return (
          <>
            {startsNewDate && (
              <Divider
                px="sm"
                size="sm"
                labelPosition="center"
                label={
                  currentDate.isToday()
                    ? t("Today")
                    : currentDate.add(1, "day").isToday()
                      ? t("Yesterday")
                      : currentDate.format(
                          currentDate.add(2, "day").isToday() ? "dddd" : "ddd L"
                        )
                }
              />
            )}
          </>
        )
      }}
      renderItem={({ entity: message }) => {
        if (
          LastChatPostBeforeJoiningId !== undefined &&
          LastChatPostBeforeJoiningId !== null &&
          message.chatPostId <= LastChatPostBeforeJoiningId
        ) {
          return null
        }

        return (
          <MessageListItem
            isFocused={activeSearchMessageId === message.chatPostId}
            highlight={searchTerm}
            showReadIndicator={
              chat.chatType !== "Broadcast" && message.userId === currentUserId
            }
            showUserName={chat.chatType !== ChatType.Direct}
            onInViewportChange={(inView) => {
              !message.isRead &&
                inView &&
                handleMessageInView(message.chatPostId)
            }}
            showAvatars={chat.chatType !== ChatType.Direct}
            key={message.chatPostId}
            message={message}
            readOnly={readOnly}
            onReplyButtonClick={() => onReplyButtonClick(message)}
          />
        )
      }}
    />
  )
}
